import { useEffect, useState } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useSuspenseProdutos } from '@/features/produtos/hooks'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { VagaPadraoEscolhaOpcoes } from '@/pages/proposta/etapa-informacoes-iniciais/vaga-padrao/vaga-padrao-escolha-opcoes'
import { VagaPadraoSelector } from '@/pages/proposta/etapa-informacoes-iniciais/vaga-padrao/vaga-padrao-seletor'
import { Button, Sheet, SheetContent } from '@/shared/components/ui'
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form'

import { VagaPadraoUnidadesVinculadas } from './vaga-padrao-unidades-vinculadas'

export type TVagasSelecionadas = Array<{
  nomeVaga: string
  id: number
  descricao: string
  substituiVagaPadrao?: boolean
}>

export const VagaPadraoTrigger = () => {
  const { t } = useTranslation('proposta')
  const { control, watch, setValue } = useFormContext<TPropostaSchemaInput>()
  const idExternoBlocoUnidade = watch('informacoesIniciais.simulacaoUnidade.bloco.idExterno')
  const acessoriosDaUnidade = watch('informacoesIniciais.simulacaoUnidade.produto.acessoriosDaUnidade')
  const simulacoesAcessoriosExtras = watch('informacoesIniciais.simulacoesAcessorios')
  const vagasSalvas = watch('informacoesIniciais.vagaIds')

  const { data: vagas } = useSuspenseProdutos(idExternoBlocoUnidade, 'Acessorio', true)
  const [open, setOpen] = useState(false)

  const obterEtapaDeSelecaoDeVaga = () => {
    if (acessoriosDaUnidade?.length) return 'acessoriosVinculadosUnidade'
    if (simulacoesAcessoriosExtras.length) return 'acessoriosExtrasNaProposta'
    return 'vagaPadrao'
  }

  const renderSheetContent = () => {
    const renderCase = obterEtapaDeSelecaoDeVaga()
    switch (renderCase) {
      case 'acessoriosVinculadosUnidade':
        return <VagaPadraoUnidadesVinculadas acessoriosVinculados={acessoriosDaUnidade!} />
      case 'acessoriosExtrasNaProposta': {
        const acessorios = simulacoesAcessoriosExtras.map((item) => item.produto)
        return (
          <VagaPadraoEscolhaOpcoes
            vagasSalvas={vagasSalvas}
            vagas={vagas}
            onConfirm={(selected) => {
              setValue('informacoesIniciais.vagaIds', selected, { shouldValidate: true })
              setOpen(false)
            }}
            acessorios={acessorios}
          />
        )
      }
      case 'vagaPadrao':
      default:
        return (
          <VagaPadraoSelector
            vagas={vagas}
            defaultSelecionadas={vagasSalvas}
            onConfirm={(selected) => {
              setValue('informacoesIniciais.vagaIds', selected, { shouldValidate: true })
              setOpen(false)
            }}
          />
        )
    }
  }

  useEffect(() => {
    const vagasFormatadas = acessoriosDaUnidade?.length
      ? acessoriosDaUnidade.flatMap((item) =>
          item.acessorios.map((acessorio) => ({
            id: acessorio.idExterno,
            nomeVaga: item.nome,
            descricao: acessorio.nome,
          })),
        )
      : []

    if (!vagasFormatadas?.length) return

    setValue('informacoesIniciais.vagaIds', vagasFormatadas)
  }, [setValue, acessoriosDaUnidade])

  return (
    <FormField
      control={control}
      name="informacoesIniciais.vagaIds"
      render={() => (
        <FormControl>
          <FormItem>
            <Sheet modal open={open} onOpenChange={setOpen}>
              <Button variant="default" onClick={() => setOpen(true)} className="w-full">
                {t('informacoes_iniciais.cards.vaga_padrao.vincule')}
              </Button>
              <SheetContent
                closeClassname="right-12"
                preventDismiss
                className="bg-foreground :max-w-4xl w-full gap-8 overflow-hidden pr-12 pl-8 sm:max-w-md md:max-w-2xl"
              >
                {open && renderSheetContent()}
              </SheetContent>
            </Sheet>
            <FormMessage />
          </FormItem>
        </FormControl>
      )}
    />
  )
}
